import { useEffect, useState } from "react";
import { useParams } from "wouter";
import { Loader2, CheckCircle2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

interface Charge {
  id: number;
  description: string;
  total: string | number;
  transactionDate: string;
}

interface ChargesResponse {
  memberId: number;
  memberName: string;
  charges: Charge[];
  totalAmount: number;
}

type PageState =
  | { kind: "loading" }
  | { kind: "data"; payload: ChargesResponse }
  | { kind: "no-charges"; payload: ChargesResponse }
  | { kind: "expired" }
  | { kind: "consumed" }
  | { kind: "confirmed" }
  | { kind: "error"; message: string };

export default function SelfServiceConfirm() {
  const params = useParams<{ token: string }>();
  const token = params.token;
  const [state, setState] = useState<PageState>({ kind: "loading" });
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [disputeReason, setDisputeReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ kind: "error", message: "Invalid token" });
      return;
    }
    fetch(`/api/m/confirm/${encodeURIComponent(token)}`, { credentials: "omit" })
      .then(async (res) => {
        if (res.status === 410) {
          const body = (await res.json().catch(() => null)) as { error?: string } | null;
          if (body?.error === "Token expired") {
            setState({ kind: "expired" });
          } else {
            setState({ kind: "consumed" });
          }
          return;
        }
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as { error?: string } | null;
          setState({ kind: "error", message: body?.error ?? `HTTP ${res.status}` });
          return;
        }
        const payload = (await res.json()) as ChargesResponse;
        if (payload.charges.length === 0) {
          setState({ kind: "no-charges", payload });
        } else {
          setState({ kind: "data", payload });
          setSelectedIds(new Set(payload.charges.map((c) => c.id)));
        }
      })
      .catch((err: Error) => setState({ kind: "error", message: err.message }));
  }, [token]);

  async function postConfirm(disputeText?: string) {
    if (!token) return;
    setSubmitting(true);
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      toast.error("Select at least one charge");
      setSubmitting(false);
      return;
    }
    try {
      const res = await fetch(`/api/m/confirm/${encodeURIComponent(token)}`, {
        method: "POST",
        credentials: "omit",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ chargeIds: ids, disputeReason: disputeText }),
      });
      if (res.status === 410) {
        setState({ kind: "consumed" });
        return;
      }
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        toast.error(body?.error ?? `HTTP ${res.status}`);
        return;
      }
      setState({ kind: "confirmed" });
      setDisputeOpen(false);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background flex justify-center">
      <div className="w-full max-w-[400px] px-4 py-6 text-base">
        {/* Brand header */}
        <div className="text-center mb-6">
          <div className="text-2xl font-semibold">Hoblix</div>
          <div className="text-sm text-muted-foreground">Coworking Space</div>
        </div>

        {/* Loading state */}
        {state.kind === "loading" ? (
          <div className="flex flex-col items-center text-muted-foreground py-12">
            <Loader2 className="h-8 w-8 animate-spin mb-3" />
            Verifying your link…
          </div>
        ) : null}

        {/* Token expired */}
        {state.kind === "expired" ? (
          <div className="flex flex-col items-center text-muted-foreground py-12">
            <Clock className="h-10 w-10 mb-3" />
            <div className="text-2xl font-semibold mb-2 text-foreground">This link has expired</div>
            <div className="text-sm text-center">
              Links are valid for 24 hours. Ask the reception team to send a new one.
            </div>
          </div>
        ) : null}

        {/* Token already consumed */}
        {state.kind === "consumed" ? (
          <div className="flex flex-col items-center py-12">
            <CheckCircle2 className="h-10 w-10 mb-3 text-green-600" />
            <div className="text-2xl font-semibold mb-2">Already confirmed</div>
            <div className="text-sm text-center text-muted-foreground">
              You already confirmed these charges. Nothing more to do.
            </div>
          </div>
        ) : null}

        {/* Post-confirm success */}
        {state.kind === "confirmed" ? (
          <div className="flex flex-col items-center py-12">
            <CheckCircle2 className="h-12 w-12 mb-3 text-green-600 transition-opacity duration-300" />
            <div className="text-2xl font-semibold mb-2">Confirmed!</div>
            <div className="text-sm text-center text-muted-foreground">
              Your charges have been recorded. You'll receive your monthly invoice on your billing day.
            </div>
          </div>
        ) : null}

        {/* No pending charges */}
        {state.kind === "no-charges" ? (
          <div className="flex flex-col items-center py-12">
            <CheckCircle2 className="h-12 w-12 mb-3 text-green-600" />
            <div className="text-2xl font-semibold mb-2">You're all clear!</div>
            <div className="text-sm text-center text-muted-foreground">
              Hi {state.payload.memberName}, no pending charges from the last hour. Check back after your next usage.
            </div>
          </div>
        ) : null}

        {/* Charges present — main confirm flow */}
        {state.kind === "data" ? (
          <>
            <div className="text-2xl font-semibold mb-2">Hi {state.payload.memberName},</div>
            <div className="text-sm text-muted-foreground mb-6">
              Please confirm your recent usage.
            </div>

            <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">
              Charges from last hour
            </div>

            <div className="space-y-3 mb-6">
              {state.payload.charges.map((c) => (
                <Card key={c.id}>
                  <CardContent className="p-3 flex items-center justify-between">
                    <div>
                      <div className="text-base">{c.description}</div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(c.transactionDate).toLocaleString("en-IN", {
                          hour: "2-digit",
                          minute: "2-digit",
                          day: "2-digit",
                          month: "short",
                        })}
                      </div>
                    </div>
                    <div className="font-mono text-base font-semibold">
                      Rs. {Number(c.total).toFixed(2)}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="flex justify-between text-xl font-semibold mb-6">
              <span>Total:</span>
              <span className="font-mono">Rs. {Number(state.payload.totalAmount).toFixed(2)}</span>
            </div>

            {/* Primary CTA: h-12 for minimum 48px touch target */}
            <Button
              className="h-12 w-full mb-3"
              onClick={() => void postConfirm(undefined)}
              disabled={submitting}
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Confirm Usage
            </Button>

            {/* Secondary CTA: dispute */}
            <Button
              variant="outline"
              className="h-10 w-full"
              onClick={() => setDisputeOpen(true)}
              disabled={submitting}
            >
              Dispute a charge
            </Button>

            {/* Dispute dialog — bottom-sheet style on mobile */}
            <Dialog open={disputeOpen} onOpenChange={setDisputeOpen}>
              <DialogContent className="max-w-[360px]">
                <DialogHeader>
                  <DialogTitle>Dispute charges</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  {state.payload.charges.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={selectedIds.has(c.id)}
                        onCheckedChange={(v) => {
                          const next = new Set(selectedIds);
                          if (v) {
                            next.add(c.id);
                          } else {
                            next.delete(c.id);
                          }
                          setSelectedIds(next);
                        }}
                      />
                      <span className="text-sm">{c.description}</span>
                    </label>
                  ))}
                  <Textarea
                    placeholder="Describe the issue (required)"
                    value={disputeReason}
                    onChange={(e) => setDisputeReason(e.target.value)}
                    rows={4}
                    className="text-base"
                  />
                </div>
                <DialogFooter>
                  <Button
                    variant="destructive"
                    className="h-12 w-full"
                    onClick={() => {
                      if (disputeReason.trim().length < 5) {
                        toast.error("Please describe the issue (≥5 chars)");
                        return;
                      }
                      void postConfirm(disputeReason.trim());
                    }}
                    disabled={submitting}
                  >
                    {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                    Flag for Review
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        ) : null}

        {/* Error fallback */}
        {state.kind === "error" ? (
          <div className="text-center py-12 text-red-600">
            <div className="text-base font-semibold mb-1">Something went wrong</div>
            <div className="text-sm">{state.message}</div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
